import * as React from "react"
import { createFileRoute, useRouter } from "@tanstack/react-router"
import { useServerFn } from "@tanstack/react-start"
import { LogIn } from "lucide-react"
import { toast } from "sonner"

import { AuthLinks, AuthPanel, DemoCredentials } from "@/components/auth-panel"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { loginFn } from "@/lib/app.functions"

export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>) => ({
    next:
      typeof search.next === "string" && search.next.startsWith("/")
        ? search.next
        : undefined,
  }),
  head: () => ({
    meta: [{ title: "Demo sign in · Harbor & Pine Realty" }],
  }),
  component: LoginPage,
})

function LoginPage() {
  const login = useServerFn(loginFn)
  const router = useRouter()
  const search = Route.useSearch()
  const [email, setEmail] = React.useState("")
  const [password, setPassword] = React.useState("")
  const [pending, setPending] = React.useState(false)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setPending(true)
    const form = new FormData(event.currentTarget)
    try {
      const result = await login({
        data: {
          email: String(form.get("email") ?? ""),
          password: String(form.get("password") ?? ""),
          remember: form.get("remember") === "on",
        },
      })
      if (!result.ok) {
        toast.error(result.error)
        return
      }
      if (search.next) {
        window.location.assign(search.next)
        return
      }
      await router.invalidate()
      toast.success(`Welcome, ${result.user.name}.`)
      await router.navigate({
        to: result.user.role === "admin" ? "/admin" : "/dashboard",
      })
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to sign in.")
    } finally {
      setPending(false)
    }
  }

  return (
    <AuthPanel
      eyebrow="Demo access"
      title="Sign in"
      intro="Use one of the shared accounts below. Do not enter a real password or private information."
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="email" className="field-label">
            Email
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="username"
            className="field-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password" className="field-label">
            Password
          </Label>
          <Input
            id="password"
            name="password"
            type="password"
            minLength={8}
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            className="field-input"
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox name="remember" />
          Keep this demo session for 30 days
        </label>
        <Button
          type="submit"
          className="field-button w-full"
          size="lg"
          disabled={pending}
        >
          <LogIn />
          {pending ? "Opening…" : "Open workspace"}
        </Button>
      </form>
      <div className="-mx-8 mt-7 bg-foreground px-8 py-1 text-white">
        <DemoCredentials
          onChoose={(nextEmail, nextPassword) => {
            setEmail(nextEmail)
            setPassword(nextPassword)
          }}
        />
      </div>
      <AuthLinks mode="login" />
    </AuthPanel>
  )
}
